import { prospectExpectedPercent, type ProspectPace } from '../../shared/prospect-engagement';

// One bar for every prospect, on the tracker and on his People profile. The fill
// runs from orange (just started) to green (goal met), so Scott can scan the length
// and the color without reading numbers. The thin tick marks where today's pace
// says he should be: fill past the tick is ahead, short of it is behind.
export function ProspectProgressBar({
  earned,
  goal,
  progressPercent,
  pace,
  size = 'sm',
}: {
  earned: number;
  goal: number;
  progressPercent: number;
  pace: ProspectPace;
  size?: 'sm' | 'md';
}) {
  const paceLabel = pace === 'on_track' ? 'on track' : pace;
  const expected = prospectExpectedPercent();
  // Any logged touch shows a visible sliver, even at 1% of the goal.
  const width = earned > 0 ? Math.max(progressPercent, 3) : 0;
  return (
    <div className={size === 'md' ? 'w-full' : 'mt-1 max-w-[300px]'}>
      <div
        className={[
          'mb-1 flex items-center justify-between gap-2 font-mono text-muted',
          size === 'md' ? 'text-[11px]' : 'text-[10px]',
        ].join(' ')}
      >
        <span>{progressPercent}%</span>
        <span>
          {earned.toFixed(1)} / {goal} pts ·{' '}
          <span className={pace === 'ahead' ? 'text-flag-ok' : pace === 'behind' ? 'text-flag-warn' : undefined}>
            {paceLabel}
          </span>
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progressPercent}
        aria-label={`${progressPercent}% of the ${goal}-point goal, ${paceLabel}`}
        className={[
          'relative overflow-hidden rounded-full bg-border/70',
          size === 'md' ? 'h-2.5' : 'h-2',
        ].join(' ')}
      >
        {width > 0 && (
          <div
            className="h-full rounded-full bg-gradient-to-r from-flag-warn to-flag-ok"
            // Stretch the gradient across the whole track so the color shows how
            // close he is to the goal, not just the two ends of the fill.
            style={{ width: `${width}%`, backgroundSize: `${(100 / width) * 100}% 100%` }}
          />
        )}
        <div
          aria-hidden="true"
          title={`Pace today: ${expected}%`}
          className="absolute inset-y-0 w-0.5 bg-ink/50"
          style={{ left: `${expected}%` }}
        />
      </div>
    </div>
  );
}
